import { useState } from 'react';

/**
 * A product's photograph in a list row, with a larger preview on hover.
 *
 * The preview is `position: fixed` and placed from the cell's own rectangle,
 * which looks like more machinery than it should need. It is not: the table sits
 * inside `.mk-twrap`, which is `overflow-x: auto` so a wide table can scroll, and
 * an overflow container CLIPS absolutely-positioned descendants — including
 * vertically, because `overflow-x: auto` with a visible `overflow-y` computes to
 * `auto` as well. An absolutely-positioned popup here is drawn and then cut off,
 * which is indistinguishable from one that never opened.
 *
 * Fixed positioning takes the preview out of that container entirely. The cost is
 * measuring on hover; the alternative is a preview that silently does not appear.
 * (An in-frame `scale()` on the 48px thumbnail was tried instead and reverted:
 * clipped to its own frame, it was too small to recognise the piece by.)
 */
export function RowImage({ url, label }: { url: string | undefined; label: string }) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  if (!url) return <div className="mk-rowimg-none" title="No images">◌</div>;

  const open = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const SIZE = 300;
    // Kept on screen: below the fold it flips upward, and near the right edge it
    // opens to the left of the cell instead.
    const top = Math.min(Math.max(8, r.top + r.height / 2 - SIZE / 2), window.innerHeight - SIZE - 8);
    const left = r.right + 12 + SIZE > window.innerWidth ? r.left - SIZE - 12 : r.right + 12;
    setAt({ top, left });
  };

  return (
    <div
      className="mk-rowimg"
      tabIndex={0}
      onMouseEnter={(e) => open(e.currentTarget)}
      onMouseLeave={() => setAt(null)}
      onFocus={(e) => open(e.currentTarget)}
      onBlur={() => setAt(null)}
    >
      <img src={url} alt="" loading="lazy" />
      {at ? (
        <div className="mk-rowimg-pop" style={{ top: at.top, left: at.left }} aria-hidden="true">
          <img src={url} alt="" />
          <span>{label}</span>
        </div>
      ) : null}
    </div>
  );
}
